import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const inputCls =
  "w-full bg-white border border-rule rounded-sm px-4 py-3 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-green/40 focus:border-green transition-colors duration-200";

export default function EnquiryForm({ source = "website", dark = false, compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", need: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = new URLSearchParams({ "form-name": "enquiry", ...form, source });
      const resp = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const previewHost = /localhost|preview\.emergentagent\.com/.test(window.location.hostname);
      if (!resp.ok && !previewHost) throw new Error("Submission failed");
      setDone(true);
      toast.success(previewHost ? "Preview mode — on Netlify this lands in your Forms dashboard." : "Enquiry sent. We'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`border rounded-sm p-10 text-center ${dark ? "border-paper/20 bg-paper/5" : "border-rule bg-green-lt/50"}`} data-testid="enquiry-success">
        <CheckCircle2 className="mx-auto mb-4 text-green" size={36} />
        <p className={`font-serif text-2xl mb-2 ${dark ? "text-paper" : "text-ink"}`}>Thank you, {form.name.split(" ")[0]}.</p>
        <p className={`text-sm ${dark ? "text-paper/70" : "text-ink-mid"}`}>Your enquiry is in. Expect a reply at {form.email} within one business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form" data-netlify="true" name="enquiry">
      <input type="hidden" name="form-name" value="enquiry" />
      <div className={compact ? "space-y-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <input required name="name" value={form.name} onChange={set("name")} placeholder="Name" className={inputCls} data-testid="enquiry-name-input" />
        <input required type="email" name="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} data-testid="enquiry-email-input" />
        <input name="phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} data-testid="enquiry-phone-input" />
        <input name="company" value={form.company} onChange={set("company")} placeholder="Company" className={inputCls} data-testid="enquiry-company-input" />
      </div>
      <textarea
        name="need"
        value={form.need}
        onChange={set("need")}
        placeholder="What do you need? (e.g. monthly accounting, part-time CFO, fundraise support)"
        rows={compact ? 3 : 4}
        className={inputCls}
        data-testid="enquiry-need-input"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="enquiry-submit-btn"
        className="inline-flex items-center gap-2 bg-green text-white text-sm font-medium px-7 py-3.5 rounded-sm hover:bg-green-mid transition-colors duration-200 disabled:opacity-60"
      >
        {loading && <Loader2 size={15} className="animate-spin" />}
        {loading ? "Sending…" : "Send enquiry"}
      </button>
    </form>
  );
}
